/**
 * Welcome-to-Research empty-state computation.
 *
 * Shared by every view that mounts the ResearchWelcomePanel — currently
 * App.tsx (mainView === 'workspace' path) and BuildArtifactsView's
 * RequirementsPhase. Keeping a single source of truth here prevents the
 * Report and Graph tabs from disagreeing on whether to show the welcome
 * panel when the heuristic evolves.
 *
 * "Welcome state" = no real research has happened yet:
 *   - no report content (whitespace-only doesn't count)
 *   - no non-`question` graph nodes (the backend seeds a question node
 *     from the user's typed prompt — that alone does NOT count as
 *     research output)
 *   - no findings
 */
import type { DictWorkspaceState } from '../types/workspaceState';
import type { ProjectDocument } from '../apiService';

type WelcomeProject = Pick<ProjectDocument, 'draft_report' | 'findings'>;

export function computeIsWelcomeState(
  dictWorkspace: DictWorkspaceState | null | undefined,
  activeProject: WelcomeProject | null | undefined,
): boolean {
  const reportText =
    (dictWorkspace?.reports && Object.values(dictWorkspace.reports)[0]?.content) ||
    activeProject?.draft_report ||
    '';
  const hasReportContent = reportText.trim().length > 0;

  const researchNodeCount = dictWorkspace?.nodes
    ? Object.values(dictWorkspace.nodes).filter((n) => n.node_type !== 'question').length
    : 0;

  const findingsCount = Object.keys(
    dictWorkspace?.findings || activeProject?.findings || {},
  ).length;

  return !hasReportContent && researchNodeCount === 0 && findingsCount === 0;
}
